/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import birdata from "../api.js";
import ko from "knockout";
import _ from "underscore";
import validate from "../util/validate.js";
import util from "util/util.js";

var program = {
	code: "biby",
	name: "Birds in Backyards",
	description: "Birds in Backyards is a research, education and conservation program that aims to understand and encourage a diversity of native birds that live where people live.",
	speciesURLs: require("./biby-urls.json"),
	load: function() {
		return birdata.lookup().then(function(lookups) {
			program.lookups = lookups;
			program.options.experience = lookups.biby_experience;
			program.options.surveyPeriod = _.filter(lookups.biby_survey_period, function(item) { return item.code != 'year'; });
		});
	},
	options: {
		projects: [
			'Birds in Schools',
			'Birds Qld Garden Bird Survey',
			'Gang-gang Cockatoo project',
			'Safe Cat, Safe Wildlife',
			'Summer Survey (Dec - Feb)',
			'Autumn Survey (Mar - May)',
			'Winter Survey (Jun - Aug)',
			'Spring Survey (Sep - Nov)'
		]
	},
	hideSurveyType: true,
	surveyTypeOptions: [],
	fields: function() {
		var fields = {
			site: ko.observable(),
			surveyPeriod: ko.observable(),
			defaultSpecies: ko.observable([]),
			experience: ko.observable(),
			project: ko.observable(),

			siteCondition: {
				plantTypes: ko.observable(),
				treeTypes: ko.observable(),

				gardenSize: ko.observable(),

				lawnCoverage: ko.observable(),
				smallPlantCoverage: ko.observable(),
				shrubCoverage: ko.observable(),
				treeCoverage: ko.observable(),
				hardSurfaceCoverage: ko.observable(),

				dogFrequency: ko.observable(),
				catFrequency: ko.observable(),
				aviaryBirdFrequency: ko.observable(),

				feedTypes: ko.observableArray(),
				otherFeedDescription: ko.observable(),
				waterAvailable: ko.observable(),

				nestBoxes: ko.observableArray([])
			}
		};

		var feedTypes = fields.siteCondition.feedTypes;
		feedTypes.otherSelected = ko.pureComputed(() => feedTypes().some(c => c.code == "other"));

		return fields;
	},
	loadFromJSON: function(json, fields, survey) {
		var data = json.biby;

		if(!data) return; //incomplete survey

		var lookups = program.lookups; // assuming lookups already loaded

		fields.project(json.biby.project);
		fields.experience(lookups.find('biby_experience', data.experience));
		fields.site(program.existingSite(json.survey_point, json.biby.site_condition));
		fields.surveyPeriod(lookups.find('biby_survey_period', data.survey_period));

		program.loadSiteConditionFromJSON(data.site_condition, fields.siteCondition);
	},
	updateSightingsFromJSON: function(json, sightings, survey) {
		// Interactions:
		if(json.biby) {
			_.each(json.biby.interactions, function(ji) {
				_.each(sightings, function(sighting) {
					if(ji.initiator_species_id == sighting.species.id) {
						var si = sighting.bibyInteraction;
						si.interactionObserved(true);
						si.aggressive(ji.aggressive);

						birdata.speciesList().then(function(speciesList) {
							si.targetSpecies(_.findWhere(speciesList, { id: ji.target_species_id}));
						});

						birdata.lookup().then(function(lookups) {
							si.possibleReason(lookups.find('biby_interaction_reason', ji.possible_reason));
							si.outcome(lookups.find('biby_interaction_outcome', ji.outcome));
						});
					}
				});
			});
		}

		// Surveys submitted via the app might have sightings with count = 0
		// BIBY requires a count for all sightings so we change this to count = 1
		var counts = {};
		_.each(json.sightings, function(s) {
			counts[s.species_id] = s.count;
		});
		_.each(sightings, function(sighting) {
			if(counts[sighting.species.id] == 0) {
				sighting.count("1");
			}
		});
	},
	updateSurveyJSON: function(json, survey) {
		function code(f) { return f ? f.code : null; }
		function num(f) { return f ? +f : null; }

		var biby = survey.programData().biby.fields;

		// Survey level fields
		json.biby = {
			experience: code(biby.experience()),
			survey_period: code(biby.surveyPeriod()),
			project: biby.project() || null
		};

		var site = biby.site();

		if(site) {

			// Site condition
			var sc = biby.siteCondition;
			json.biby.site_condition = {
				plant_types: code(sc.plantTypes()),
				tree_types: code(sc.treeTypes()),

				garden_size: code(sc.gardenSize()),

				lawn_coverage: code(sc.lawnCoverage()),
				small_plant_coverage: code(sc.smallPlantCoverage()),
				shrub_coverage: code(sc.shrubCoverage()),
				tree_coverage: code(sc.treeCoverage()),
				hard_surface_coverage: code(sc.hardSurfaceCoverage()),

				dog_frequency: code(sc.dogFrequency()),
				cat_frequency: code(sc.catFrequency()),
				aviary_bird_frequency: code(sc.aviaryBirdFrequency()),

				feed_types: sc.feedTypes().map(code),
				other_feed_description: sc.feedTypes.otherSelected() ? sc.otherFeedDescription() : null,
				water_available: sc.waterAvailable(),

				nest_boxes: _.map(sc.nestBoxes(), program.nestBoxJSON)
			};


			// Site
			if(site.id) {
				json.biby.site_condition.site = {
					survey_point_id: site.id
				};
			} else {
				json.biby.site_condition.site = {
					address1: site.address1(),
					address2: site.address2(),
					suburb: site.suburb(),
					postcode: site.postcode(),
					state: site.state(),
					type: site.type().code
				};
			}
		}

		json.biby.interactions = [];
		_.each(survey.sightings(), function(sighting) {
			var i = sighting.bibyInteraction;

			if(sighting.isPresent() && i.interactionObserved()) {
				json.biby.interactions.push({
					initiator_species_id: sighting.species.id,
					target_species_id: i.targetSpecies().id,
					aggressive: i.aggressive(),
					possible_reason: i.possibleReason() && i.possibleReason().code,
					outcome: i.outcome() && i.outcome().code
				});
			}
		});
	},
	loadSiteConditionFromJSON: function(json, siteCondition) {
		var lookups = program.lookups; // assumes that lookups are loaded
		var l = lookups.find; // shorthand for brevity

		siteCondition.plantTypes(l('biby_plant_types', json.plant_types));
		siteCondition.treeTypes(l('biby_plant_types', json.tree_types));

		siteCondition.gardenSize(l('biby_garden_size', json.garden_size));

		siteCondition.lawnCoverage(l('biby_coverage', json.lawn_coverage));
		siteCondition.smallPlantCoverage(l('biby_coverage', json.small_plant_coverage));
		siteCondition.shrubCoverage(l('biby_coverage', json.shrub_coverage));
		siteCondition.treeCoverage(l('biby_coverage', json.tree_coverage));
		siteCondition.hardSurfaceCoverage(l('biby_coverage', json.hard_surface_coverage));

		// TODO: remove legacy '_new' checks
		siteCondition.dogFrequency(l('biby_animal_frequency', json.dog_frequency_new || json.dog_frequency));
		siteCondition.catFrequency(l('biby_animal_frequency', json.cat_frequency_new || json.cat_frequency));
		siteCondition.aviaryBirdFrequency(l('biby_animal_frequency', json.aviary_bird_frequency_new || json.aviary_bird_frequency));

		siteCondition.feedTypes((json.feed_types || []).map(code => l('biby_feed_type', code)));
		siteCondition.otherFeedDescription(json.other_feed_description);
		siteCondition.waterAvailable(json.water_available);

		var nestBoxes = _.map(json.nest_boxes || [], function(json) {
			var nestBox = program.nestBoxModel();
			program.loadNestBoxFromJSON(json, nestBox);
			return nestBox;
		});
		siteCondition.nestBoxes(nestBoxes);
	},
	loadNestBoxFromJSON: function(json, nestBox) {
		nestBox.sizeInCm(json.size_in_cm);

		var occupantTypeCode = json.occupant_species_id ? "bird" : json.occupant_description ? "other" : "none";
		nestBox.occupantType(_.findWhere(program.nestBoxOccupantTypes, { code: occupantTypeCode }));

		nestBox.occupantDescription(json.occupant_description);

		birdata.speciesList().then(function(speciesList) {
			nestBox.occupantSpecies(_.findWhere(speciesList, { id: json.occupant_species_id}));
		});
	},
	nestBoxJSON: function(nestBox) {
		var json = {
			size_in_cm: ("" + nestBox.sizeInCm()).trim()
		};

		if(nestBox.occupantType()) {
			if(nestBox.occupantType().code == "bird") {
				json.occupant_species_id = nestBox.occupantSpecies() ? nestBox.occupantSpecies().id : null;
			}

			if(nestBox.occupantType().code == "other") {
				json.occupant_description = nestBox.occupantDescription();
			}
		}

		return json;
	},
	nestBoxOccupantTypes: [
		{
			name: "No",
			code: "none"
		}, {
			name: "Occupied by a bird",
			code: "bird"
		}, {
			name: "Occupied by another animal",
			code: "other"
		}
	],
	nestBoxModel: function() {
		var nestBox = {
			sizeInCm: ko.observable(),
			occupantType: ko.observable(),
			occupantDescription: ko.observable(),
			occupantSpecies: ko.observable(),
		};

		var info = {tab: 'biby_site_condition'};
		nestBox.validations = [
			validate.field(nestBox.sizeInCm, "Nest box size", info)
				.required().isInt().build(),
			validate.field(nestBox.occupantType, "Nest box occupant", info)
				.required().build(),
			validate.field(nestBox.occupantDescription, "Nest box occupant", info)
				.required().onlyIf(function(val) {
					return nestBox.occupantType() && nestBox.occupantType().code == "other";
				}).build(),
			validate.field(nestBox.occupantSpecies, "Nest box occupant", info)
				.required().onlyIf(function(val) {
					return nestBox.occupantType() && nestBox.occupantType().code == "bird";
				}).build()
		];

		return nestBox;
	},
	sites: _.memoize(function(userId){
		var params = {};
		if(userId) {
			params.user_id = userId;
		}
		return birdata.surveyPoints(params).then(function(surveyPoints) {
			return _.chain(surveyPoints).filter(function(sp) {
				return 'biby' in sp;
			}).map(function(sp) {
				return program.existingSite(sp, sp.biby.site_condition);
			}).value();
		});
	}),
	newSite: function() {
		var site = {
			name: "New site",
			address1: ko.observable(''),
			address2: ko.observable(''),
			suburb: ko.observable(''),
			postcode: ko.observable(''),
			state: ko.observable(null),
			type: ko.observable(null)
		};
		var info = {tab: 'biby_site'};
		site.validations = [
			validate.field(site.address1, "Address", info).required().build(),
			validate.field(site.suburb, "Suburb", info).required().build(),
			validate.field(site.postcode, "Postcode", info).required().build(),
			validate.field(site.state, "State", info).required().build(),
			validate.field(site.type, "Site type", info).required().build(),
		];
		return site;
	},
	existingSite: function(surveyPoint, siteCondition) {
		var site = siteCondition.site;
		return _.defaults({
			name: surveyPoint.name,
			address1: ko.observable(site.address1),
			address2: ko.observable(site.address2),
			suburb: ko.observable(site.suburb),
			postcode: ko.observable(site.postcode),
			state: ko.observable(site.state),
			type: ko.observable(program.lookups.find('biby_site_type', site.type)),
			site_condition: siteCondition
		}, surveyPoint);
	},
	programData: function(survey) {
		var fields = program.fields();


		function isBirdsInSchools() {
			return fields.project() == 'Birds in Schools';
		}

		function validateSiteCondition() {
			return fields.site() && !isBirdsInSchools();
		}

		var biby = {
			fields: fields,
			isBirdsInSchools: isBirdsInSchools
		};

		// Setup
		ko.computed(function() {
			if(fields.surveyPeriod() && survey.program() == program) {
				switch(fields.surveyPeriod().code) {
					case '20_minutes':
						survey.durationMinutes(20);
						survey.durationHours(0);
						survey.multipleDays(false);
						break;
					case 'week':
						survey.multipleDays(true);
						break;
				}
			}
		});


		// Setup validations
		var scValidations = _.map({
			plantTypes: 'Plant types',
			treeTypes: 'Tree types',
			gardenSize: 'Garden size',
			lawnCoverage: 'Lawn coverage',
			treeCoverage: 'Tree coverage',
			smallPlantCoverage: 'Small Plant coverage',
			shrubCoverage: 'Shrub coverage',
			hardSurfaceCoverage: 'Hard surface coverage',
			dogFrequency: 'Dogs – frequency',
			catFrequency: 'Cats – frequency',
			aviaryBirdFrequency: 'Aviary Birds/Chickens – frequency',
			waterAvailable: 'Is there usually a water source available for birds?'
		}, function(label, name) {
			return validate.field(fields.siteCondition[name], label, {tab: 'biby_site_condition'})
				.required()
				.onlyIf(validateSiteCondition).build();
		});

		var validations = _.flatten([
			scValidations,
			validate.field(fields.surveyPeriod, 'Survey type')
				.required().build(),
			validate.field(fields.siteCondition.otherFeedDescription, "Other food(s) provided", { tab: "biby_site_condition" })
				.required()
				.onlyIf(function() { return validateSiteCondition() && fields.siteCondition.feedTypes.otherSelected(); })
				.build(),
			validate.field(fields.project, "Project").build(),
			validate.field(fields.experience, "Experience").required().build()
		]);

		// Note that we compose the validations into a list inside pureComputed, but we don't actually
		// create the validations in the pureComputed, otherwise validations would get re-created on
		// each evaluation

		biby.validations = ko.pureComputed(function() {
			return _.flatten([
				validations,
				fields.site() ? (fields.site().validations || []) : [],
				_.pluck(fields.siteCondition.nestBoxes(), 'validations') // see biby-site-condition.js
			]);
		});

		ko.computed(function() {
			if(survey.program() == program) {
				var coords = survey.coords();
				survey.defaultSpecies([]);
				if(coords) {
					coords = coords.split(",");
					birdata.commonSpecies(coords[0], coords[1]).then(survey.defaultSpecies);
				}
			} else {
				survey.defaultSpecies([]);
			}
		});

		biby.options = program.options;

		return biby;
	},
	beforeSelect: function(survey) {
		birdata.lookup().then(function(lookup) {
			survey.type(lookup.find('survey_type', 'incidental_search'));
		});
		survey.assignOwner(false);
	},
	beforeDeselect: function(survey) {
		survey.type(null);
	},
	validateSurveyPoint: function(surveyPoint) {
	},
	speciesList: ko.observable([]),
	multipleDaysEnabled: function(survey) {
		return false;
	},
	fixedNumberOfDays: function(survey) {
		let period = bibyData(survey)?.surveyPeriod?.()?.code
		return (period == 'week') ? 7 : 0;
	},
	durationEnabled: function(survey) {
		let period = bibyData(survey)?.surveyPeriod?.()?.code
		return period != '20_minutes';
	},
	canAssignSurveyOwner: false
};

function bibyData(survey) {
	return survey.programData()?.biby?.fields;
}

export default program;
